import React, { useState } from "react";
import {
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItemText,
  ListItemButton,
  Divider,
  Typography,
  Box,
} from "@mui/material";
import { Email as EmailIcon } from "@mui/icons-material";
import { EmailThreadComponent } from "../../components/EmailThreadComponent";

interface LinkedEmail {
  id: string;
  messageId: string;
  threadId: string;
  inboxEmail: string | null;
}

interface LinkedMessagesListProps {
  emails: LinkedEmail[];
  size?: "small" | "medium" | "large";
}

export const LinkedMessagesList: React.FC<LinkedMessagesListProps> = ({
  emails,
  size = "medium",
}) => {
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<LinkedEmail | null>(null);

  const handleEmailClick = (email: LinkedEmail) => {
    setSelectedEmail(email);
    setEmailModalOpen(true);
  };

  if (emails.length === 0) {
    return null;
  }

  return (
    <>
      <IconButton
        size={size}
        onClick={() => setEmailModalOpen(true)}
        title={`View ${emails.length} linked message${emails.length > 1 ? "s" : ""}`}
      >
        <EmailIcon />
        {emails.length > 1 && (
          <Typography
            variant="caption"
            sx={{
              position: "absolute",
              top: -2,
              right: -2,
              backgroundColor: "primary.main",
              color: "white",
              borderRadius: "50%",
              width: 16,
              height: 16,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.6rem",
            }}
          >
            {emails.length}
          </Typography>
        )}
      </IconButton>

      <Dialog
        open={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <EmailIcon />
            <Typography variant="h6">
              Linked Messages ({emails.length})
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {emails.length === 1 ? (
            // Single email - show thread directly
            <EmailThreadComponent
              // threadId={emails[0].threadId}
              emailId={emails[0].id}
              // inboxEmail={emails[0].inboxEmail || undefined}
              // selectedMessageId={emails[0].messageId}
            />
          ) : (
            // Multiple emails - show list to choose from
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select a message to view:
              </Typography>
              <List>
                {emails.map((email, index) => (
                  <React.Fragment key={email.id}>
                    <ListItemButton
                      onClick={() => handleEmailClick(email)}
                      sx={{ py: 1 }}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="body2" fontWeight="medium">
                            Message ID: {email.messageId}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="body2" color="text.secondary">
                            Thread ID: {email.threadId}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                    {index < emails.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Individual email thread dialog */}
      {selectedEmail && (
        <Dialog
          open={!!selectedEmail}
          onClose={() => setSelectedEmail(null)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <EmailIcon />
              <Typography variant="h6">
                Message {selectedEmail.messageId}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <EmailThreadComponent
              emailId={selectedEmail.id}
              // threadId={selectedEmail.threadId}
              // inboxEmail={selectedEmail.inboxEmail || undefined}
              // selectedMessageId={selectedEmail.messageId}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSelectedEmail(null)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};
